import React, { useEffect, useState, useCallback } from 'react';
import { useControllerWebSocket } from '../hooks/useControllerWebSocket';
import { useAudioProcessor } from '../hooks/useAudioProcessor';
import './Controller.css';

// Use localhost:3001 in development (direct connection) or window.location.origin in production
const SERVER_URL = process.env.NODE_ENV === 'development' && window.location.port === '3000'
  ? 'http://localhost:3001' // Direct connection to Express server in development
  : window.location.origin;

interface ControllerProps {
  gameId: string;
}

export function Controller({ gameId }: ControllerProps) {
  const [playerId, setPlayerId] = useState<1 | 2 | null>(null);
  const [deviceId, setDeviceId] = useState<string>('');
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'failed' | 'full'>('connecting');
  const [showReplaceOptions, setShowReplaceOptions] = useState(false);
  const [sleepPrevention, setSleepPrevention] = useState<{
    isActive: boolean;
    method: 'wakeLock' | 'video' | 'interval' | 'none';
    wakeLock: any | null;
  }>({
    isActive: false,
    method: 'none',
    wakeLock: null
  });

  const [countdownSeconds, setCountdownSeconds] = useState<number | null>(null);
  const [gameStarted, setGameStarted] = useState(false);
  const [gameEnded, setGameEnded] = useState(false);
  const [gamePaused, setGamePaused] = useState(false);
  const [pausedWaitingFor, setPausedWaitingFor] = useState<1 | 2 | null>(null);
  const [winner, setWinner] = useState<1 | 2 | null>(null);
  const [finalScore, setFinalScore] = useState<{ player1: number; player2: number } | null>(null);
  const [isReadyForNewGame, setIsReadyForNewGame] = useState(false);
  const [readyCount, setReadyCount] = useState(0);
  const [totalPlayers, setTotalPlayers] = useState(2);
  const { socket, connected, reconnecting, error, joinGame, sendPitchData } = useControllerWebSocket(SERVER_URL);

  // Audio processing
  const {
    isCapturing,
    currentPitch,
    currentVolume,
    error: audioError,
    permissionGranted,
    calibrationRange,
    startCapture,
    stopCapture,
    calibrate
  } = useAudioProcessor({
    volumeThreshold: 0.01,
    sampleRate: 44100
  });

  // Debug logging (removed for production)

  // Send pitch data immediately when detected - no rate limiting or buffering
  useEffect(() => {
    if (deviceId && isCapturing && connected && currentPitch) {
      sendPitchData(gameId, deviceId, currentPitch);
    }
  }, [deviceId, currentPitch, gameId, isCapturing, connected, sendPitchData]);

  useEffect(() => {
    if (connected && gameId) {
      // Join as controller
      const generatedDeviceId = generateDeviceId();
      setDeviceId(generatedDeviceId);
      joinGame(gameId, generatedDeviceId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected, gameId]); // Intentionally excluding joinGame to prevent multiple join attempts

  // Automatically enable microphone when controller opens and player is assigned
  useEffect(() => {
    if (playerId && !isCapturing && !audioError && connectionStatus === 'connected') {
      startCapture();
    }
  }, [playerId, isCapturing, audioError, connectionStatus, startCapture]);

  useEffect(() => {
    if (socket) {
      const handleMessage = (message: any) => {
        // Handle incoming messages

        switch (message.type) {
          case 'playerAssignment':
            if (message.success && message.playerId) {
              setPlayerId(message.playerId);
              setConnectionStatus('connected');
            } else if (!message.success) {
              setConnectionStatus('full');
              setShowReplaceOptions(true);
            }
            break;
          case 'countdown':
            setCountdownSeconds(message.seconds);
            setGameStarted(false);
            break;
          case 'gameStart':
            setCountdownSeconds(null);
            setGameStarted(true);
            setGameEnded(false);
            setIsReadyForNewGame(false);
            break;
          case 'gameEnd':
            setGameEnded(true);
            setGameStarted(false);
            setWinner(message.winner);
            setFinalScore(message.finalScore);
            setIsReadyForNewGame(false);
            break;
          case 'playerReadyForNewGame':
            setReadyCount(message.readyCount);
            setTotalPlayers(message.totalPlayers);
            break;
          case 'gamePaused':
            setGamePaused(true);
            setPausedWaitingFor(message.waitingForPlayer);
            setGameStarted(false);
            break;
          case 'gameResumed':
            setGamePaused(false);
            setPausedWaitingFor(null);
            break;
          case 'playerReconnected':
            // Handle player reconnection notification
            console.log(`Player ${message.playerId} reconnected`);
            break;
          case 'error':
            if (message.message === 'Both player slots are full') {
              setConnectionStatus('full');
              setShowReplaceOptions(true);
            } else {
              setConnectionStatus('failed');
            }
            break;
          default:
            break;
        }
      };

      socket.on('message', handleMessage);

      return () => {
        socket.off('message', handleMessage);
      };
    }
  }, [socket]);

  // Sleep prevention functions with enhanced debugging
  const requestSleepPrevention = useCallback(async () => {
    console.log('🔒 Attempting to activate sleep prevention...');
    console.log('🔍 Browser capabilities:', {
      wakeLockSupported: 'wakeLock' in navigator,
      userAgent: navigator.userAgent,
      platform: navigator.platform
    });

    try {
      // Method 1: Wake Lock API (most reliable)
      if ('wakeLock' in navigator) {
        console.log('🔒 Trying Wake Lock API...');
        const wakeLockSentinel = await (navigator as any).wakeLock.request('screen');
        
        setSleepPrevention({
          isActive: true,
          method: 'wakeLock',
          wakeLock: wakeLockSentinel
        });

        wakeLockSentinel.addEventListener('release', () => {
          console.log('⚠️ Wake lock released by system');
          setSleepPrevention(prev => ({ ...prev, isActive: false, wakeLock: null }));
        });

        console.log('✅ Wake Lock API activated successfully!');
        return true;
      }

      console.log('⚠️ Wake Lock API not supported, trying video fallback...');

      // Method 2: Video element fallback with better error handling
      const video = document.createElement('video');
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('loop', '');
      video.setAttribute('autoplay', '');
      video.style.position = 'fixed';
      video.style.top = '-1px';
      video.style.left = '-1px';
      video.style.width = '1px';
      video.style.height = '1px';
      video.style.opacity = '0.01';
      video.style.pointerEvents = 'none';
      video.style.zIndex = '-1000';
      
      // Use a simpler, more compatible video source
      const canvas = document.createElement('canvas');
      canvas.width = 1;
      canvas.height = 1;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = 'black';
        ctx.fillRect(0, 0, 1, 1);
      }
      
      // Try to create a video stream from canvas
      try {
        const stream = (canvas as any).captureStream ? (canvas as any).captureStream(1) : null;
        if (stream) {
          video.srcObject = stream;
          console.log('📹 Using canvas stream for video fallback');
        } else {
          // Fallback to data URL
          video.src = 'data:video/mp4;base64,AAAAHGZ0eXBpc29tAAACAGlzb21pc28ybXA0MQAAAAhmcmVlAAAAG21kYXQAAAGzABAHAAABthADAowdbb9/AAAC6W1vb3YAAABsbXZoZAAAAAB8JbCAfCWwgAAAA+gAAAAAAAEAAAEAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAIAAAIVdHJhawAAAFx0a2hkAAAAD3wlsIB8JbCAAAAAAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAQAAAAAALAAAACgAAAAAAJGVkdHMAAAAcZWxzdAAAAAAAAAABAAAAAQAACgAAAAABAAAAAAG2bWRpYQAAACBtZGhkAAAAAHwlsIB8JbCAAAAACgAAAAAVxAAAAAAALWhkbHIAAAAAAAAAAHZpZGUAAAAAAAAAAAAAAABWaWRlb0hhbmRsZXIAAAABYW1pbmYAAAAUdm1oZAAAAAEAAAAAAAAAAAAAACRkaW5mAAAAHGRyZWYAAAAAAAAAAQAAAAx1cmwgAAAAAQAAAQFzdGJsAAAAr3N0c2QAAAAAAAAAAQAAAJ9hdmMxAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAAAALQAuABIAAAASAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAGP//AAAALWF2Y0MBQsAN/+EAFWdCwA3ZAsTsBEAAAPpAADqYA8UKkgEABWjLg8sgAAAAHHV1aWRraEDyXyRPxbo5pRvPAyPzAAAAAAAAABhzdHRzAAAAAAAAAAEAAAABAAAACgAAAAAUc3RzcwAAAAAAAAABAAAAAQAAAAEAAAAcc3RzYwAAAAAAAAABAAAAAQAAAAEAAAABAAAAFHN0c3oAAAAAAAAAEwAAAAEAAAAUc3RjbwAAAAAAAAABAAAALAAAABB1ZHRhAAAACGNvcHlyaWdodAAAAP9tZXRhAAAAAAAAACFoZGxyAAAAAAAAAABtZGlyYXBwbAAAAAAAAAAAAAAAAC1pbHN0AAAAJal9dG9vAAAAHWRhdGEAAAABAAAAAExhdmY1Ni40MC4xMDE=';
          console.log('📹 Using data URL for video fallback');
        }
      } catch (streamError) {
        console.log('⚠️ Canvas stream failed, using data URL:', streamError);
        video.src = 'data:video/mp4;base64,AAAAHGZ0eXBpc29tAAACAGlzb21pc28ybXA0MQAAAAhmcmVlAAAAG21kYXQAAAGzABAHAAABthADAowdbb9/AAAC6W1vb3YAAABsbXZoZAAAAAB8JbCAfCWwgAAAA+gAAAAAAAEAAAEAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAIAAAIVdHJhawAAAFx0a2hkAAAAD3wlsIB8JbCAAAAAAQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAQAAAAAAAAAAAAAAAAAAQAAAAAALAAAACgAAAAAAJGVkdHMAAAAcZWxzdAAAAAAAAAABAAAAAQAACgAAAAABAAAAAAG2bWRpYQAAACBtZGhkAAAAAHwlsIB8JbCAAAAACgAAAAAVxAAAAAAALWhkbHIAAAAAAAAAAHZpZGUAAAAAAAAAAAAAAABWaWRlb0hhbmRsZXIAAAABYW1pbmYAAAAUdm1oZAAAAAEAAAAAAAAAAAAAACRkaW5mAAAAHGRyZWYAAAAAAAAAAQAAAAx1cmwgAAAAAQAAAQFzdGJsAAAAr3N0c2QAAAAAAAAAAQAAAJ9hdmMxAAAAAAAAAAEAAAAAAAAAAAAAAAAAAAAAALQAuABIAAAASAAAAAAAAAABAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAGP//AAAALWF2Y0MBQsAN/+EAFWdCwA3ZAsTsBEAAAPpAADqYA8UKkgEABWjLg8sgAAAAHHV1aWRraEDyXyRPxbo5pRvPAyPzAAAAAAAAABhzdHRzAAAAAAAAAAEAAAABAAAACgAAAAAUc3RzcwAAAAAAAAABAAAAAQAAAAEAAAAcc3RzYwAAAAAAAAABAAAAAQAAAAEAAAABAAAAFHN0c3oAAAAAAAAAEwAAAAEAAAAUc3RjbwAAAAAAAAABAAAALAAAABB1ZHRhAAAACGNvcHlyaWdodAAAAP9tZXRhAAAAAAAAACFoZGxyAAAAAAAAAABtZGlyYXBwbAAAAAAAAAAAAAAAAC1pbHN0AAAAJal9dG9vAAAAHWRhdGEAAAABAAAAAExhdmY1Ni40MC4xMDE=';
      }
      
      document.body.appendChild(video);
      
      try {
        const playPromise = video.play();
        if (playPromise) {
          await playPromise;
        }
        console.log('✅ Video fallback sleep prevention activated successfully!');
        
        setSleepPrevention({
          isActive: true,
          method: 'video',
          wakeLock: { video }
        });
        
        return true;
      } catch (videoError) {
        console.warn('❌ Video fallback failed:', videoError);
        if (video.parentNode) {
          document.body.removeChild(video);
        }
      }

      console.log('⚠️ Video fallback failed, trying interval method...');

      // Method 3: Interval-based fallback with more aggressive approach
      let counter = 0;
      const intervalId = setInterval(() => {
        counter++;
        // Multiple techniques to keep the page active
        // eslint-disable-next-line no-self-assign
        document.title = document.title;
        
        // Touch the DOM slightly
        if (counter % 10 === 0) {
          const timestamp = Date.now();
          document.body.setAttribute('data-awake', timestamp.toString());
        }
        
        // Log every minute to verify it's working
        if (counter % 120 === 0) { // Every 60 seconds (30s * 2)
          console.log('🔄 Interval sleep prevention still active');
        }
      }, 30000); // Every 30 seconds

      setSleepPrevention({
        isActive: true,
        method: 'interval',
        wakeLock: { intervalId }
      });

      console.log('✅ Interval-based sleep prevention activated');
      return true;

    } catch (error) {
      console.error('❌ All sleep prevention methods failed:', error);
      return false;
    }
  }, []);

  const releaseSleepPrevention = useCallback(async () => {
    if (sleepPrevention.wakeLock) {
      try {
        if (sleepPrevention.method === 'wakeLock' && sleepPrevention.wakeLock.release) {
          await sleepPrevention.wakeLock.release();
        } else if (sleepPrevention.method === 'video' && sleepPrevention.wakeLock.video) {
          sleepPrevention.wakeLock.video.pause();
          if (sleepPrevention.wakeLock.video.parentNode) {
            sleepPrevention.wakeLock.video.parentNode.removeChild(sleepPrevention.wakeLock.video);
          }
        } else if (sleepPrevention.method === 'interval' && sleepPrevention.wakeLock.intervalId) {
          clearInterval(sleepPrevention.wakeLock.intervalId);
        }
        
        setSleepPrevention({
          isActive: false,
          method: 'none',
          wakeLock: null
        });
        
        console.log('Sleep prevention released');
      } catch (error) {
        console.error('Failed to release sleep prevention:', error);
      }
    }
  }, [sleepPrevention]);



  // Handle visibility change to re-acquire sleep prevention if needed
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && sleepPrevention.isActive && !sleepPrevention.wakeLock) {
        requestSleepPrevention();
      }
    };

    const handleUserInteraction = () => {
      if (sleepPrevention.isActive && !sleepPrevention.wakeLock) {
        requestSleepPrevention();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('touchstart', handleUserInteraction, { passive: true });
    document.addEventListener('click', handleUserInteraction, { passive: true });

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('touchstart', handleUserInteraction);
      document.removeEventListener('click', handleUserInteraction);
    };
  }, [sleepPrevention, requestSleepPrevention]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (sleepPrevention.wakeLock) {
        if (sleepPrevention.method === 'wakeLock' && sleepPrevention.wakeLock.release) {
          sleepPrevention.wakeLock.release().catch(console.error);
        } else if (sleepPrevention.method === 'video' && sleepPrevention.wakeLock.video) {
          sleepPrevention.wakeLock.video.pause();
          if (sleepPrevention.wakeLock.video.parentNode) {
            sleepPrevention.wakeLock.video.parentNode.removeChild(sleepPrevention.wakeLock.video);
          }
        } else if (sleepPrevention.method === 'interval' && sleepPrevention.wakeLock.intervalId) {
          clearInterval(sleepPrevention.wakeLock.intervalId);
        }
      }
    };
  }, [sleepPrevention]);

  const handleReplacePlayer = (playerToReplace: 1 | 2) => {
    if (socket && gameId && deviceId) {
      // Send replace player message to server
      const message = {
        type: 'replacePlayer',
        gameId,
        deviceId, // Use existing deviceId
        playerToReplace
      };

      socket.emit('message', message);

      // Hide replace options and show connecting status
      setShowReplaceOptions(false);
      setConnectionStatus('connecting');
    }
  };

  const handleReadyForNewGame = () => {
    if (socket && gameId && deviceId && gameEnded) {
      const message = {
        type: 'readyForNewGame',
        gameId,
        deviceId
      };

      socket.emit('message', message);
      setIsReadyForNewGame(true);
    }
  };

  const renderConnectionStatus = () => {
    switch (connectionStatus) {
      case 'connecting':
        return (
          <div className="status-container">
            <div className="spinner"></div>
            <h2>Connecting to game...</h2>
            <p>Game ID: {gameId}</p>
          </div>
        );

      case 'connected':
        return (
          <div className="status-container success">
            <h2>Connected as Player {playerId}!</h2>
            <p>Game ID: {gameId}</p>
            <div className="player-indicator">
              <div className={`player-badge player-${playerId}`}>
                Player {playerId}
              </div>
            </div>

            {/* Countdown Display */}
            {countdownSeconds !== null && (
              <div className="countdown-display">
                <h3>Game Starting In...</h3>
                <div className="countdown-number">
                  {countdownSeconds}
                </div>
                <p>Get ready to use your voice!</p>
              </div>
            )}

            {/* Game Paused Notification */}
            {gamePaused && (
              <div className="game-paused">
                <h3>⏸️ Game Paused</h3>
                {pausedWaitingFor === playerId ? (
                  <p>You disconnected. Rejoin to resume the game!</p>
                ) : (
                  <p>Waiting for Player {pausedWaitingFor} to rejoin...</p>
                )}
              </div>
            )}

            {/* Game Started Notification */}
            {gameStarted && !gameEnded && !gamePaused && (
              <div className="game-started">
                <h3>🎮 Game Started!</h3>
                <p>Use your voice to control your paddle</p>
              </div>
            )}

            {/* Game End Notification */}
            {gameEnded && (
              <div className="game-ended">
                <h3>🏆 Game Over!</h3>
                {winner && finalScore && (
                  <div className="game-result">
                    <div className={`winner-announcement ${winner === playerId ? 'you-won' : 'you-lost'}`}>
                      {winner === playerId ? '🎉 You Won!' : `Player ${winner} Won!`}
                    </div>
                    <div className="final-score">
                      Final Score: {finalScore.player1} - {finalScore.player2}
                    </div>
                  </div>
                )}
                
                <div className="new-game-section">
                  {!isReadyForNewGame ? (
                    <button
                      className="ready-button"
                      onClick={handleReadyForNewGame}
                    >
                      Ready for New Game
                    </button>
                  ) : (
                    <div className="waiting-for-players">
                      <p>✓ You're ready for a new game!</p>
                      <p>Waiting for other players... ({readyCount}/{totalPlayers})</p>
                      {readyCount === totalPlayers && (
                        <p>Starting new game...</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Audio Controls */}
            <div className="audio-controls">
              {!permissionGranted && !audioError && (
                <div className="audio-permission">
                  <p>Enable voice control to play:</p>
                  <button
                    className="audio-button primary"
                    onClick={startCapture}
                  >
                    Enable Microphone
                  </button>
                </div>
              )}

              {audioError && (
                <div className="audio-error">
                  <p>Microphone Error: {audioError}</p>
                  <button
                    className="audio-button"
                    onClick={startCapture}
                  >
                    Retry
                  </button>
                </div>
              )}

              {permissionGranted && (
                <div className="audio-status">
                  <div className="audio-controls-row">
                    <button
                      className={`audio-button ${isCapturing ? 'stop' : 'start'}`}
                      onClick={isCapturing ? stopCapture : startCapture}
                    >
                      {isCapturing ? 'Stop Voice Control' : 'Start Voice Control'}
                    </button>
                    <button
                      className="audio-button secondary"
                      onClick={calibrate}
                      disabled={!isCapturing}
                    >
                      Recalibrate
                    </button>
                  </div>

                  {/* Manual Sleep Lock Control */}
                  <div className="sleep-lock-controls">
                    <button
                      className="audio-button secondary"
                      onClick={sleepPrevention.isActive ? releaseSleepPrevention : requestSleepPrevention}
                    >
                      {sleepPrevention.isActive ? '🔓 Release Sleep Lock' : '🔒 Enable Sleep Lock'}
                    </button>
                  </div>

                  {isCapturing && (
                    <div className="pitch-display">
                      <div className="pitch-info">
                        <div className="pitch-value">
                          <label>Current Pitch:</label>
                          <span className={`pitch-number ${currentPitch ? 'active' : 'inactive'}`}>
                            {currentPitch ? `${Math.round(currentPitch)} Hz` : 'No pitch detected'}
                          </span>
                        </div>

                        <div className="volume-meter">
                          <label>Volume:</label>
                          <div className="volume-bar">
                            <div
                              className="volume-fill"
                              style={{ width: `${Math.min(currentVolume * 1000, 100)}%` }}
                            />
                          </div>
                          <span className="volume-value">
                            {(currentVolume * 100).toFixed(1)}%
                          </span>
                        </div>

                        {calibrationRange && (
                          <div className="calibration-info">
                            <label>Pitch Range:</label>
                            <span className="range-display">
                              {Math.round(calibrationRange.min)} - {Math.round(calibrationRange.max)} Hz
                            </span>
                          </div>
                        )}

                        {/* Sleep Prevention Status */}
                        <div className="sleep-prevention-status">
                          <label>Screen Lock:</label>
                          <span className={`status-indicator ${sleepPrevention.isActive ? 'active' : 'inactive'}`}>
                            {sleepPrevention.isActive ? `🔒 ${sleepPrevention.method === 'wakeLock' ? 'Wake Lock' : sleepPrevention.method === 'video' ? 'Video Fallback' : 'Interval Fallback'} Active` : '⚠️ Not Active'}
                          </span>
                        </div>


                      </div>

                      <div className="pitch-instructions">
                        <p>🎵 Hum or sing to control your paddle!</p>
                        <p>Higher pitch = paddle moves up</p>
                        <p>Lower pitch = paddle moves down</p>
                        {/* Sleep Prevention Hints */}
                        {sleepPrevention.isActive && sleepPrevention.method !== 'wakeLock' && (
                          <p style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
                            💡 Keep this tab active to prevent screen sleep
                          </p>
                        )}
                        {!sleepPrevention.isActive && (
                          <p style={{ fontSize: '12px', color: '#fbbf24', marginTop: '10px' }}>
                            💡 Use "Enable Sleep Lock" button to prevent screen from sleeping
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );

      case 'full':
        return (
          <div className="status-container warning">
            <h2>Game is Full</h2>
            <p>Both player slots are occupied.</p>
            {showReplaceOptions && (
              <div className="replace-options">
                <p>Would you like to replace an existing player?</p>
                <div className="button-group">
                  <button
                    className="replace-button"
                    onClick={() => handleReplacePlayer(1)}
                  >
                    Replace Player 1
                  </button>
                  <button
                    className="replace-button"
                    onClick={() => handleReplacePlayer(2)}
                  >
                    Replace Player 2
                  </button>
                </div>
                <button
                  className="cancel-button"
                  onClick={() => setShowReplaceOptions(false)}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        );

      case 'failed':
        return (
          <div className="status-container error">
            <h2>Connection Failed</h2>
            <p>Unable to connect to the game.</p>
            {error && <p className="error-message">Error: {error}</p>}
            <button
              className="retry-button"
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="controller">
      <div className="controller-header">
        <h1>SingPong Controller</h1>
        <div className="connection-indicator">
          <span className={`connection-dot ${connected ? 'connected' : reconnecting ? 'reconnecting' : 'disconnected'}`}></span>
          <span>{connected ? 'Connected' : reconnecting ? 'Reconnecting...' : 'Disconnected'}</span>
        </div>
      </div>

      <main className="controller-main">
        {renderConnectionStatus()}
      </main>
    </div>
  );
}

function generateDeviceId(): string {
  // Generate a unique device ID for this controller session
  return 'controller-' + Math.random().toString(36).substring(2, 11) + '-' + Date.now();
}